// src/encounter/lib/nursing-plan-proposals.ts
//
// PROD-2947 Part 2 · frame v2 + planSave · contract K9 ("Plan list: nursing problems, goals, interventions — lines
// proposed from today's findings or from the nurse library, each with Add or Dismiss"). The nurse's INTERVENTIONS,
// proposed from the nurse library (phrase-library-nurse-v1, PROD-2951) for the nursing problems she ACCEPTED today.
//
// WHERE THE WORDS COME FROM — never written here (encounter authority: "Nothing clinical may be invented"):
//   · the library's starter map (`nurse-plan-library.json`, a copy kept equal to scripts/templates/library/nurse by
//     the test): each visit reason has `matchHints` (words, not codes — authority §3) and a P list of phrase keys;
//   · an accepted problem matches a visit reason when one of its hints is a WHOLE word or phrase of the problem's
//     text ("Morse Fall Scale: 50" matches "fall"; "below range" never matches "low");
//   · the reason's P phrases of the library's `education` family are proposed, in the P list's order, by their
//     TITLE as the library writes it ("Education — home blood pressure monitoring"). Education is a nursing
//     intervention (authority §2: "interventions (monitoring, education)").
//   · GOALS: the library's only goal phrase (`nurse-plan-goal`, "Goal discussed with the patient") is a blank form —
//     it holds no goal. So no goal is proposed; that is a content gap for the template lane, not a gap to fill.
//   · the other nursing-plan phrases (summary, problem, actions, evaluation, pillars, handover, follow-up,
//     safety-net) are documentation forms with blanks, not plan lines; follow-up lives in Wrap-up (L5).
//
// Stored like the problems (./nursing-problems.ts codec) in the existing `nursing-interventions` entry, under its own
// heading — no server change. Pure: no React, no fetch.
import library from './nurse-plan-library.json';
import { encodePlanLines, parsePlanLines, type NursingProblem, type NursingProblemRecord } from './nursing-problems';

/** The nursing plan entry the interventions live in (./nursing-plan.ts field id; renaming orphans text). */
export const NURSING_INTERVENTIONS_FIELD = 'nursing-interventions';
/** The format marker line, as for the problems: lines are read as interventions only after it. */
export const INTERVENTIONS_HEADING = "Interventions for today's nursing problems:";
/** The library family whose phrases are proposed as interventions. */
export const EDUCATION_FAMILY = 'education';

interface VisitReason {
  id: string;
  label: string;
  matchHints: readonly string[];
  P: readonly string[];
}
interface PlanPhrase {
  title: string;
  family: string;
}
const REASONS = library.visitReasons as readonly VisitReason[];
const PHRASES = library.planPhrases as Readonly<Record<string, PlanPhrase>>;

const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A hint as a whole word or phrase, any case (letters and digits on either side end a match). */
const hintPattern = (hint: string): RegExp => new RegExp(`(^|[^\\p{L}\\p{N}])${escape(hint.trim())}($|[^\\p{L}\\p{N}])`, 'iu');

/** The library's visit reasons whose hints the problem's words carry, in the starter map's order. */
export function reasonsFor(problem: Pick<NursingProblem, 'text'>): readonly VisitReason[] {
  return REASONS.filter((r) => r.matchHints.some((h) => h.trim() !== '' && hintPattern(h).test(problem.text)));
}

/**
 * The interventions the library proposes for these accepted problems: each problem in order, each matched reason in
 * order, each of its education phrases in P order — by title, "for" the problem. The same title twice is proposed once
 * (the first problem it was found for).
 */
export function libraryInterventions(accepted: readonly NursingProblem[]): NursingProblem[] {
  const out: NursingProblem[] = [];
  for (const problem of accepted) {
    for (const reason of reasonsFor(problem)) {
      for (const key of reason.P) {
        const phrase = PHRASES[key];
        if (!phrase || phrase.family !== EDUCATION_FAMILY) continue;
        if (out.some((o) => o.text === phrase.title)) continue;
        out.push({ text: phrase.title, source: problem.text });
      }
    }
  }
  return out;
}

/** The `nursing-interventions` entry, read back (accepted, dismissed, earlier free text). */
export const parseInterventions = (text: string | null | undefined): NursingProblemRecord => parsePlanLines(text, INTERVENTIONS_HEADING);
/** The entry text for an interventions record. */
export const encodeInterventions = (record: NursingProblemRecord): string => encodePlanLines(record, INTERVENTIONS_HEADING);
